import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** when this changes (the page the person moved to), a failed screen is tried again */
  resetKey?: string;
  /** "page" keeps the menu usable around a failed screen; "app" is the last resort around everything */
  scope?: 'page' | 'app';
}

/** A page's code file that could not be downloaded (network dropped, or a new release replaced the file). */
const isChunkLoadError = (e: unknown) =>
  /dynamically imported module|Importing a module script failed|Loading chunk .* failed|Loading CSS chunk/i.test(String((e as Error | undefined)?.message ?? e));

/**
 * Catches an error thrown while drawing a screen, so a bug in one page shows a message with a way out instead of a blank window.
 * Around a page it sits inside the shell, so the menu still works and moving to another page recovers.
 */
export class ErrorBoundary extends Component<Props, { failed: boolean; stale: boolean }> {
  state = { failed: false, stale: false };

  static getDerivedStateFromError(error: unknown) {
    return { failed: true, stale: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Screen failed to draw', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false, stale: false });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const app = this.props.scope === 'app';
    return (
      <div className="auth-wrap" role="alert" style={app ? undefined : { minHeight: '40vh' }}>
        <div className="panel" style={{ maxWidth: '32rem', margin: '0 auto', padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 0.5rem' }}>Something went wrong</h2>
          <p className="muted" style={{ margin: '0 0 1rem' }}>
            {this.state.stale
              ? 'This page could not be downloaded, usually because the connection dropped. Nothing you saved was lost. Check your connection and try again.'
              : <>
                {app ? 'The application hit an unexpected problem.' : 'This page hit an unexpected problem.'} Nothing you saved was lost.
                {app ? ' Reload to continue.' : ' Try again, or open another page from the menu.'}
              </>}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {app ? null : (
              <button
                type="button"
                className="btn"
                // a page file that failed to download stays failed until the browser asks for it again, so only a reload recovers it
                onClick={() => (this.state.stale ? window.location.reload() : this.setState({ failed: false }))}
              >Try again</button>
            )}
            <button type="button" className="btn primary" onClick={() => window.location.reload()}>Reload the application</button>
          </div>
        </div>
      </div>
    );
  }
}
